import TermsLink from "@iscr/views/UIelems/TermsLinks";
import { meets_minimum_version } from "@iscr/xAppLib/helpers/meets_minimum_version";
import { useSiteStatus } from "@iscr/xAppLib/Hooks";
import ReCaptcha from "@iscr/xAppLib/UIelems/ReCapacha";
import { validateAuthFields } from "helpers/signupValidation";
import { createRef, useReducer, useState } from 'react';
import { Button, Checkbox, Form } from "semantic-ui-react";
import SIcon from "views/NUI/StreamlineIcons/SIcon";
import { NotificationBar } from "views/UIelems/v2/notificationBar/NotificationBar";
import { TextField } from "../../../views/UIelems/v2/Form/TextField";
import { PasswordChecklist } from "./PasswordChecklist";
import { NoMedicareCardModal } from "@iscr/views/ordering/components/NoMedicareCardModal";
import { PointerLabel } from "../../../views/UIelems/v2/PointerLabel";

function must_verify_captcha(flag) {
    if (typeof flag === 'boolean') return flag;
    if (typeof flag !== 'string') return false;

    if (['true', 'false'].includes(flag)) {
        // Just in case someone sets it as a boolean string
        return flag === 'true';
    }

    return meets_minimum_version(flag);
}

const UserSignUpAccount = ({ onSubmit, loading, authError }) => {
    const captchaRef = createRef();
    const key = app.settings.is_ionic ? 'sgup_captcha_ia' : 'sgup_captcha'
    const enableCaptcha = must_verify_captcha(useSiteStatus(key));

    const [state, dispatch] = useReducer(reducer, initialState(enableCaptcha));
    const [errors, setErrors] = useState({});
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    const scrollToError = (errors) => {
        const firstErrorField = Object.keys(errors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);

        if (errorElement) {
            const offset = 100;
            const elementPosition = errorElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            errorElement.focus();
        }
    };

    const clearError = (fieldName) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    };

    const handleOnSubmit = (event) => {
        event.preventDefault();
        const formErrors = validateAuthFields(state);
        setErrors(formErrors);
        const captcha = captchaRef.current;

        if (Object.keys(formErrors).length === 0) {
            if (typeof onSubmit === 'function') {
                onSubmit({
                    provider: 'email',
                    ...state,
                    consent: state.consent
                }).finally(() => {
                    captcha?.resetCaptcha?.();
                    dispatch({ type: "CLEAR_CAPTCHA" });
                });
            }
        } else {
            scrollToError(formErrors);
        }
    };

    return (
        <Form
            className="w-full flex flex-col gap-2 md:gap-4 lg:mt-9 lg:mb-[116px] lg:rounded"
            onSubmit={handleOnSubmit}
            noValidate
        >
            <div className="w-full max-w-screen-md mx-auto sm:px-4">
                <div className="bg-white pt-10 pb-6 px-6 max-w-screen-md lg:rounded">
                    <CreateAccount state={state}
                        dispatch={dispatch}
                        errors={errors}
                        clearError={clearError}
                        showPasswordFields={showPasswordFields}
                        setShowPasswordFields={setShowPasswordFields}
                        passwordVisible={passwordVisible}
                        setPasswordVisible={setPasswordVisible}
                        confirmPasswordVisible={confirmPasswordVisible}
                        setConfirmPasswordVisible={setConfirmPasswordVisible}
                    />
                    {
                        enableCaptcha && (
                            <ReCaptcha
                                ref={captchaRef}
                                onComplete={(token) => dispatch({ type: "SET_CAPTCHA", payload: token })}
                                onError={() => dispatch({ type: "CLEAR_CAPTCHA" })}
                                isRedLabel={errors.captcha}
                            />
                        )}
                </div>
                {authError && (
                    <div className="mb-2">
                        <NotificationBar type="negative" text={authError.message} className={"text-center"} />
                    </div>
                )}
                <div className="bg-white py-8 px-6 max-w-screen-md mt-1">
                    <Button data-testid="button-continue" primary fluid type="submit" loading={loading} disabled={loading}>
                        Continue
                    </Button>
                </div>
            </div>
        </Form>
    )
};

export default UserSignUpAccount;

function CreateAccount({ state, dispatch, errors, clearError, showPasswordFields, setShowPasswordFields, passwordVisible, setPasswordVisible, confirmPasswordVisible, setConfirmPasswordVisible }) {

    return (
        <div className="flex flex-col gap-3">
            <h2 className="m-0 font-bold pb-2">Account Details</h2>
            <div>
                <label className="!mb-5">Log in details:</label>
                <Form.Field className="!mb-2 !pb-0.5">
                    <TextField
                        label="Email "
                        id="email"
                        type="email"
                        name="email"
                        placeholder="Email"
                        aria-label="Email"
                        fluid={true}
                        value={state.email}
                        required
                        onChange={e => {
                            dispatch({ type: 'SET_EMAIL', payload: e.target.value });
                            if (e.target.value.trim()) clearError('email');
                        }}
                        autoFocus
                        error={errors.email}
                    />
                </Form.Field>
                <Form.Field className="!mb-5">
                    <TextField
                        label="Password "
                        id="password"
                        name="password"
                        type={passwordVisible ? "text" : "password"}
                        placeholder="Enter password"
                        aria-label="Password"
                        fluid={true}
                        value={state.password}
                        required
                        onChange={e => {
                            const password = e.target.value;
                            dispatch({ type: 'SET_PASSWORD', payload: password });
                            if (password.trim()) {
                                clearError('password');
                                setShowPasswordFields(true);
                            } else {
                                setShowPasswordFields(false);
                            }
                        }}
                        error={errors.password}
                    />
                    <button
                        onClick={(event) => {
                            event.preventDefault();
                            setPasswordVisible(!passwordVisible);
                        }}
                        className="absolute right-0 top-2 cursor-pointer z-10"
                    >
                        <SIcon name={passwordVisible ? "eye" : "eye"} size="sm" svg_cls='' cls="text-is2-gray-150 pr-3 mr-2" bold />
                    </button>
                </Form.Field>
                {showPasswordFields && (
                    <>
                        <PasswordChecklist password={state.password} />
                        <Form.Field className="!mb-5">
                            <TextField
                                label="Confirm Password "
                                id="confirmPassword"
                                name="confirmPassword"
                                type={confirmPasswordVisible ? "text" : "password"}
                                placeholder="Confirm Password"
                                aria-label="Confirm Password"
                                fluid={true}
                                value={state.confirmPassword}
                                required
                                onChange={e => {
                                    dispatch({ type: 'SET_CONFIRM_PASSWORD', payload: e.target.value });
                                    if (e.target.value.trim()) clearError('confirmPassword');
                                }}
                                error={errors.confirmPassword}
                            />
                            <button
                                onClick={(event) => {
                                    event.preventDefault();
                                    setConfirmPasswordVisible(!confirmPasswordVisible);
                                }}
                                className="absolute right-0 top-2 cursor-pointer z-10"
                            >
                                <SIcon name={confirmPasswordVisible ? "eye" : "eye"} size="sm" svg_cls='' cls="text-is2-gray-150 pr-3 mr-2" bold />
                            </button>
                        </Form.Field>
                    </>
                )}
            </div>
            <div className="mb-6">
                <label>Your details:</label>
                <p className="text-gray-700">Please enter your details exactly as it appears on your Medicare card</p>
                <div className="bg-is2-yellow-100 h-[145px] rounded-sm mb-6">
                    <div className="max-w-[327px] relative mx-auto">
                        <img src="https://storage.googleapis.com/instant-med-public/v2/medicare-card.png" className="w-[194px] h-auto absolute right-[30px] top-2" alt="Medicare card"/>
                        <PointerLabel label="First name" width="10px" position="top-[89px] left-[14px]"/>
                        <PointerLabel label="Last name" width="25px" dir="left" position="top-[89px] right-[9px]"/>
                    </div>
                </div>
                <Form.Field className="!mb-2 !pb-0.5">
                    <TextField
                        label="First Name "
                        name="firstName"
                        placeholder="E.g. James A"
                        onChange={e => {
                            dispatch({ type: 'SET_FIRST_NAME', payload: e.target.value });
                            if (e.target.value.trim()) clearError('firstName');
                        }}
                        required
                        fluid
                        value={state.firstName}
                        error={errors.firstName}
                    />
                </Form.Field>
                <Form.Field className="!mb-6">
                    <TextField
                        label="Last Name "
                        name="lastName"
                        placeholder="E.g. Citizen"
                        onChange={e => {
                            dispatch({ type: 'SET_LAST_NAME', payload: e.target.value });
                            if (e.target.value.trim()) clearError('lastName');
                        }}
                        required
                        value={state.lastName}
                        fluid
                        error={errors.lastName}
                    />
                </Form.Field>
                <NoMedicareCardModal />
            </div>
            <div>
                <label className="!mb-5">Privacy</label>
                <div className="flex flex-row gap-1">
                    <Checkbox
                        id="consent"
                        name="consent"
                        type="checkbox"
                        className="!p-0 !h-5"
                        aria-label="Consent Checkbox"
                        checked={state.consent}
                        data-testid="checkbox-privacy"
                        onChange={e => {
                            const isChecked = e.target.checked;
                            dispatch({ type: 'SET_CONSENT_STATUS', payload: isChecked });
                            clearError('consent');
                        }}
                        required
                    />
                    <label htmlFor="consent" className="text-base pl-3">
                        I have read and accept the
                        <TermsLink type={'privacy-policy'} label={' InstantScripts Privacy Policy, '}/>
                        <TermsLink type={'platform-terms-and-conditions'} label={'Platform Terms and Conditions, '}/>
                        <TermsLink type={'customer-terms-and-conditions'} label={'Customer Terms and Conditions.'}/>
                        You will be added to our subscriber list, you can unsubscribe at any time.
                    </label>
                </div>
                <div className="text-red-600 text-xs xl:text-sm">
                    {errors.consent}
                </div>
            </div>
        </div>
    )
}

function initialState(needCaptcha = false) {
    return {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        ...(needCaptcha && { captcha: null }),
        consent: false
    }
}

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIRST_NAME':
            return { ...state, firstName: action.payload };
        case 'SET_LAST_NAME':
            return { ...state, lastName: action.payload };
        case 'SET_EMAIL':
            return { ...state, email: action.payload };
        case 'SET_PASSWORD':
            return { ...state, password: action.payload };
        case 'SET_CONFIRM_PASSWORD':
            return { ...state, confirmPassword: action.payload };
        case 'SET_CONSENT_STATUS':
            return { ...state, consent: action.payload };
        case 'SET_CAPTCHA':
            return { ...state, captcha: action.payload };
        case 'CLEAR_CAPTCHA':
            return { ...state, captcha: null };
        default:
            return state;
    }
}
